import { useEffect, useMemo, useRef, useState } from 'react';
import { useItemSets, KIND_LABEL } from './collections';
import { FOLDER_COLORS } from './investments';
import { FOLDER_ICONS, iconPreset } from './folderIcons';
import { searchSkins } from './skinIndex';
import './ModalTheme.css';

/**
 * Folder editor — name, colour and picture for one of your own folders.
 *
 * Separate from FolderPickModal, which answers "which folder do these items go
 * in?". This one answers "what is this folder", and the two questions want
 * different lists: the picker ranks folders and sets by what the items in hand
 * belong to, this one ranks pictures by what a folder is usually named after.
 *
 * The same modal makes a folder and edits one. Creating used to be a
 * window.prompt for a name, which meant a new folder could not be given a
 * picture until after it existed — one round trip for something the user
 * already knew when they clicked the button.
 *
 * Pictures come from three places, in the order they are worth offering:
 *
 *   presets      the seven category icons drawn for this app (see folderIcons)
 *   items        anything in the skins database with a picture — the King on
 *                the Field sticker, the Katowice 2014 capsule, a knife
 *   collections  the set icons, for folders that stand for a collection
 *
 * Presets stay pinned above the search because they are the answer for the
 * folders people actually make ("Knives", "Rifles", "Cases"), and no query
 * finds them: they are not items.
 *
 * Props:
 *   folder     – the folder to edit, or null to create one
 *   folders    – the folders that exist now, for the duplicate-name warning
 *   skinsData  – the skins database, searched for item pictures
 *   variant    – 'zone' | 'classic' skin, see ModalTheme.css
 *   onSave     – ({ name, color, image }) => void
 *   onClose    – () => void
 */

// Two characters before the item search runs: one letter matches thousands of
// things and none of them is what was meant.
const MIN_QUERY = 2;

export default function FolderEditModal({
  folder = null,
  folders = [],
  skinsData = [],
  variant = 'classic',
  onSave,
  onClose,
}) {
  const creating = !folder;

  const [name, setName]   = useState(folder?.name || '');
  const [color, setColor] = useState(folder?.color || FOLDER_COLORS[folders.length % FOLDER_COLORS.length]);
  const [image, setImage] = useState(folder?.image || null);
  const [query, setQuery] = useState('');

  const nameRef = useRef(null);
  const pressedBackdrop = useRef(false);

  const index = useItemSets();

  useEffect(() => {
    const onKey = e => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  useEffect(() => { nameRef.current?.focus(); }, []);

  const q = query.trim();

  // Sets first and items second, because a query that matches a collection by
  // name ("Huntsman", "Kilowatt") almost always meant the collection — the
  // items in it match the same word only because they are named after it.
  const setHits = useMemo(() => {
    if (q.length < MIN_QUERY) return [];
    const lower = q.toLowerCase();
    return index.sets.filter(s => s.image && s.name.toLowerCase().includes(lower)).slice(0, 12);
  }, [index.sets, q]);

  const itemHits = useMemo(() => searchSkins(skinsData, q, 40), [skinsData, q]);

  const trimmed = name.trim();
  const ready   = trimmed.length > 0;
  const clash   = trimmed.length > 0 && folders.some(
    f => f.id !== folder?.id && f.name?.trim().toLowerCase() === trimmed.toLowerCase());

  const save = () => {
    if (!ready) return;
    onSave?.({ name: trimmed, color, image: image || null });
    onClose?.();
  };

  const title = creating ? 'New folder' : `Edit "${folder.name}"`;

  return (
    <div className={`mdl-overlay ${variant}`}
      onMouseDown={e => { pressedBackdrop.current = e.target === e.currentTarget; }}
      onClick={e => { if (e.target === e.currentTarget && pressedBackdrop.current) onClose?.(); }}>
      <div className="mdl-panel narrow" role="dialog" aria-modal="true" aria-label={title}>

        <div className="mdl-head">
          <div className="mdl-head-txt">
            <div className="mdl-title">{title}</div>
            <div className="mdl-sub">
              A folder is just a name for a pile of units. The picture and the colour are
              how you find it on the page — neither changes what is inside it.
            </div>
          </div>
          <button className="mdl-x" onClick={onClose} aria-label="Close">×</button>
        </div>

        <div className="mdl-body">
          <div className="mdl-stack gap">

            {/* ── Name and colour ───────────────────────────── */}
            <div className="mdl-card">
              <div className="mdl-card-title">Name</div>
              <input ref={nameRef} className="mdl-input" value={name}
                placeholder="Knives, Cases, Q4 exit…"
                onChange={e => setName(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); save(); } }} />
              {clash && (
                <div className="mdl-card-hint" style={{ marginTop: 6 }}>
                  Another folder is already called <strong>{trimmed}</strong>. Two with the same
                  name still work, they are just hard to tell apart.
                </div>
              )}

              <div className="mdl-card-title" style={{ marginTop: 14 }}>Colour</div>
              <div className="mdl-card-hint">
                {image
                  ? 'Drawn only where the picture does not fit — the picture wins on the folder itself.'
                  : 'The chip beside the folder name.'}
              </div>
              <div className="fem-colors">
                {FOLDER_COLORS.map(c => (
                  <button key={c} type="button"
                    className={`fem-color${color === c ? ' on' : ''}`}
                    style={{ background: c }}
                    aria-label={`Colour ${c}`}
                    aria-pressed={color === c}
                    onClick={() => setColor(c)} />
                ))}
              </div>
            </div>

            {/* ── Picture ───────────────────────────────────── */}
            <div className="mdl-card">
              <div className="mdl-card-title">Picture</div>

              <div className="fem-current">
                {image
                  ? <img src={image} alt="" />
                  : <span className="fem-current-blank" style={{ background: color }} />}
                <div className="fem-current-txt">
                  <div className="fem-current-name">
                    {!image
                      ? 'No picture'
                      : (iconPreset(image)?.label || 'This folder has a picture')}
                  </div>
                  <div className="fem-current-sub">
                    {image
                      ? 'It is drawn instead of the colour chip, in both themes.'
                      : 'The colour chip is drawn instead. Pick one below to change that.'}
                  </div>
                </div>
                {image && (
                  <button type="button" className="mdl-btn sm" onClick={() => setImage(null)}>
                    Remove
                  </button>
                )}
              </div>

              <div className="mdl-card-title" style={{ marginTop: 14 }}>Recommended</div>
              <div className="fem-icons">
                {FOLDER_ICONS.map(ic => (
                  <button key={ic.id} type="button"
                    className={`fem-icon${image === ic.src ? ' on' : ''}`}
                    title={ic.label}
                    onClick={() => setImage(ic.src)}>
                    <img src={ic.src} alt="" />
                    <span>{ic.label}</span>
                  </button>
                ))}
              </div>

              <div className="mdl-card-title" style={{ marginTop: 14 }}>Or use any item</div>
              <input className="mdl-input" style={{ marginTop: 8 }} value={query}
                placeholder="Search an item, sticker, capsule or collection…"
                onChange={e => setQuery(e.target.value)} />

              {q.length >= MIN_QUERY && (
                setHits.length === 0 && itemHits.length === 0 ? (
                  <div className="mdl-card-hint" style={{ marginTop: 10 }}>
                    Nothing with a picture matches that.
                  </div>
                ) : (
                  <div className="fpm-colls">
                    {setHits.map(s => (
                      <button key={`set-${s.id}`} type="button"
                        className={`fpm-coll${image === s.image ? ' on' : ''}`}
                        onClick={() => setImage(s.image)}>
                        <img src={s.image} alt="" loading="lazy" />
                        <span className="fpm-coll-name">{s.name}</span>
                        <span className="fpm-coll-n">{KIND_LABEL[s.kind] || 'set'}</span>
                      </button>
                    ))}
                    {itemHits.map(it => (
                      <button key={`item-${it.name}`} type="button"
                        className={`fpm-coll${image === it.image ? ' on' : ''}`}
                        onClick={() => setImage(it.image)}>
                        <img src={it.image} alt="" loading="lazy" />
                        <span className="fpm-coll-name">{it.name}</span>
                        <span className="fpm-coll-n">item</span>
                      </button>
                    ))}
                  </div>
                )
              )}

              {q.length > 0 && q.length < MIN_QUERY && (
                <div className="mdl-card-hint" style={{ marginTop: 10 }}>
                  Keep typing — two letters at least.
                </div>
              )}
            </div>

          </div>
        </div>

        <div className="mdl-foot">
          <span className="mdl-foot-note">
            {creating
              ? 'The folder starts empty. Move units into it from any position.'
              : 'Only the folder changes. The units inside it stay where they are.'}
          </span>
          <button className="mdl-btn" onClick={onClose}>Cancel</button>
          <button className="mdl-btn primary" onClick={save} disabled={!ready}>
            {creating ? 'Create folder' : 'Save'}
          </button>
        </div>

      </div>
    </div>
  );
}
